import { showToastMessage } from "../toast_message.js";
import { copy_to_clipboard } from "../copying/clipboard.js";
import { isCharacterCollected } from "../collect_character.js";
import { initChipScroller } from "../chip_scroller.js";
import {
    collectMediaValues,
    groupCharacters,
    matchCharacter,
    parseSearch,
    passesFilters,
    sortCharacters
} from "./character_filter.js";

export const CHARACTER_GRID_CLASSES = 'row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-2 character-grid';

function readCharacter(el){
    const collectBtn = el.querySelector('.collect-toggle');
    return {
        el,
        id: el.id,
        name: el.dataset.characterName,
        media: JSON.parse(el.dataset.characterMedia || '[]'),
        searchTerms: JSON.parse(el.dataset.characterSearchTerms || '[]'),
        totalCards: Number(el.dataset.totalCards) || 0,
        sketchCount: Number(el.dataset.sketchCount) || 0,
        get collected(){
            return collectBtn ? isCharacterCollected(collectBtn) : false;
        }
    };
}

function buildSection(group){
    const section = document.createElement('section');
    section.className = 'character-group';
    section.id = `characters-${group.id}`;
    if (group.label){
        const title = document.createElement('h3');
        title.className = 'character-group-title';
        title.id = `${section.id}-title`;
        title.textContent = group.label;
        const count = document.createElement('span');
        count.className = 'character-group-count';
        count.textContent = group.items.length;
        title.append(' ', count);
        section.setAttribute('aria-labelledby', title.id);
        section.append(title);
    }
    const grid = document.createElement('div');
    grid.className = CHARACTER_GRID_CLASSES;
    grid.append(...group.items.map(c => c.el));
    section.append(grid);
    return section;
}

function buildJumpNav(nav, groups){
    const links = groups.filter(g => g.jumpLabel).map(group => {
        const link = document.createElement('a');
        link.className = 'card-list-jump-link';
        link.href = `#characters-${group.id}`;
        link.textContent = group.jumpLabel;
        return link;
    });
    nav.replaceChildren(...(links.length > 1 ? links : []));
}

// Letter headings stick just under the toolbar, whose height changes as it wraps
function trackToolbarHeight(root, toolbar){
    const update = () => root.style.setProperty('--character-sticky-top', `${toolbar.offsetHeight}px`);
    update();
    if (typeof ResizeObserver !== 'undefined'){
        new ResizeObserver(update).observe(toolbar);
    }
}

export function initCharacterBrowser(root, { pinnedIds = new Set() } = {}){
    const toolbar = root.querySelector('[data-character-toolbar]');
    const groupsRoot = root.querySelector('[data-character-groups]');
    const searchInput = root.querySelector('[data-character-search]');
    const chips = root.querySelector('[data-character-chips]');
    const collectingChip = root.querySelector('[data-collecting-chip]');
    const sortLabel = root.querySelector('[data-sort-label]');
    const jumpNav = root.querySelector('[data-character-jump]');
    const resultCount = root.querySelector('[data-result-count]');
    const emptyState = root.querySelector('[data-character-empty]');

    const characters = [...root.querySelectorAll('.character-card')].map(readCharacter);
    const state = { search: '', collectingOnly: false, media: new Set(), sort: root.dataset.defaultSort || 'name' };
    let shown = [];

    collectMediaValues(characters).forEach(media => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'filter-chip';
        chip.setAttribute('aria-pressed', 'false');
        chip.dataset.mediaChip = media;
        chip.textContent = media;
        chips.append(chip);
    });

    const filtersActive = () => Boolean(state.search.trim()) || state.collectingOnly || state.media.size > 0;

    const render = () => {
        const searchParts = parseSearch(state.search);
        const filters = { collectingOnly: state.collectingOnly, media: [...state.media] };
        const visible = characters.filter(character => passesFilters(character, filters) && matchCharacter(character, searchParts));

        shown = sortCharacters(visible, state.sort);
        const groups = groupCharacters(shown, { sortKey: state.sort, pinnedIds });
        groupsRoot.replaceChildren(...groups.map(buildSection));
        buildJumpNav(jumpNav, groups);

        resultCount.textContent = filtersActive()
            ? `Showing ${shown.length} of ${characters.length} characters`
            : `${characters.length} characters`;
        toolbar.querySelector('[data-clear-filters]').hidden = !filtersActive();
        emptyState.hidden = shown.length > 0;
    };

    const clearFilters = () => {
        state.search = '';
        state.collectingOnly = false;
        state.media.clear();
        searchInput.value = '';
        root.querySelectorAll('[data-character-chips] .filter-chip').forEach(chip => chip.setAttribute('aria-pressed', 'false'));
        render();
    };

    const copyShown = async () => {
        const text = ['Characters:', ...shown.map(c => c.name)].join('\n');
        if (await copy_to_clipboard(text)){
            showToastMessage(`Copied ${shown.length} characters to clipboard!`);
        }
        else {
            showToastMessage("Error copying to clipboard", "danger");
        }
    };

    searchInput.addEventListener('input', () => {
        state.search = searchInput.value;
        render();
    });

    root.addEventListener('click', (event) => {
        const mediaChip = event.target.closest('[data-media-chip]');
        if (mediaChip){
            const pressed = mediaChip.getAttribute('aria-pressed') !== 'true';
            mediaChip.setAttribute('aria-pressed', pressed);
            pressed ? state.media.add(mediaChip.dataset.mediaChip) : state.media.delete(mediaChip.dataset.mediaChip);
            render();
            return;
        }
        if (collectingChip && event.target.closest('[data-collecting-chip]')){
            state.collectingOnly = !state.collectingOnly;
            collectingChip.setAttribute('aria-pressed', state.collectingOnly);
            render();
            return;
        }
        const sortItem = event.target.closest('[data-sort]');
        if (sortItem){
            state.sort = sortItem.dataset.sort;
            sortLabel.textContent = sortItem.textContent;
            root.querySelectorAll('[data-sort]').forEach(item => item.classList.toggle('active', item === sortItem));
            render();
            return;
        }
        if (event.target.closest('[data-clear-filters]')){
            clearFilters();
            return;
        }
        if (event.target.closest('[data-copy-characters]')){
            copyShown();
        }
    });

    trackToolbarHeight(root, toolbar);
    root.querySelectorAll('[data-chip-scroller]').forEach(initChipScroller);
    render();
    return { render };
}
